import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { useAppStore } from '../../../app/hooks/useAppStore';
import type { Classroom, TeacherAccount } from '../../../accounts';
interface TransferClassroomModalProps {
  open: boolean;
  classroom: Classroom | null;
  activeAccount: TeacherAccount;
  onClose: () => void;
}
export function TransferClassroomModal({
  open,
  classroom,
  activeAccount,
  onClose
}: TransferClassroomModalProps) {
  const {
    accounts,
    transferClassroomOwnership
  } = useAppStore();
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [keepAsCoTeacher, setKeepAsCoTeacher] = useState(true);
  useEscapeKey(onClose, open && classroom !== null);
  if (!classroom) return null;
  // Get co-teachers
  const coTeachers = (classroom.coTeacherIds || []).map(id => accounts.accountsById[id]).filter((a): a is TeacherAccount => a?.role === 'teacher');
  // Get school colleagues (excluding co-teachers and self)
  const schoolName = activeAccount.schoolName?.trim().toLowerCase();
  const schoolColleagues = Object.values(accounts.accountsById).filter((a): a is TeacherAccount => a.role === 'teacher' && a.id !== activeAccount.id && !classroom.coTeacherIds?.includes(a.id) && a.schoolName?.trim().toLowerCase() === schoolName && schoolName !== '' && schoolName !== undefined);
  const handleTransfer = () => {
    if (!selectedTeacherId) return;
    transferClassroomOwnership(activeAccount.id, classroom.id, selectedTeacherId, keepAsCoTeacher);
    onClose();
  };
  const selectedTeacher = accounts.accountsById[selectedTeacherId];
  return <AnimatePresence data-id="element-9480" data-mp="11hi9a02u2hzc">
      {open && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="transfer-classroom-title" data-id="element-9481" data-mp="15l7xgoflixju">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-9482" data-mp="29hqo8buwot92" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-9483" data-mp="2x7l0osbbcfss">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50" data-id="element-9484" data-mp="3jhbemdrr3kiy">
              <h2 id="transfer-classroom-title" className="text-lg font-bold text-slate-900" data-id="element-9485" data-mp="3ogxpigk3wool">
                Klas "{classroom.name}" overdragen
              </h2>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors" data-id="element-9486" data-mp="1by9ecn0zkz4">
                <X className="w-5 h-5" data-id="element-9487" data-mp="b8b5fh61max1" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-9488" data-mp="fzjg0gj402bs">
              <div data-id="element-9489" data-mp="g0ur71evwccx">
                <label className="block text-sm font-bold text-slate-900 mb-2" data-id="element-9490" data-mp="g2fdk3r1oozp">
                  Wie wordt de nieuwe eigenaar?
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar" data-id="element-9491" data-mp="p1sq55agtton">
                  {coTeachers.length === 0 && schoolColleagues.length === 0 && <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-500 text-center" data-id="element-9492" data-mp="gclmb1e7ln2e">
                      Er zijn geen co-docenten of collega's gevonden op jouw
                      school.
                      <br data-id="element-9493" data-mp="q1lrx3n7vgpk" />
                      Laat een collega eerst een account aanmaken met dezelfde
                      schoolnaam.
                    </div>}

                  {coTeachers.length > 0 && <div className="mb-4" data-id="element-9494" data-mp="3tlgn63rinqaf">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9495" data-mp="3pitnxhsin337">
                        Huidige co-docenten
                      </div>
                      {coTeachers.map(t => <label key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedTeacherId === t.id ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-200'}`} data-id="element-9496" data-mp="2breajv2po49">
                          <input type="radio" name="newOwner" value={t.id} checked={selectedTeacherId === t.id} onChange={e => setSelectedTeacherId(e.target.value)} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-600" data-id="element-9497" data-mp="2vsgzevywfyf2" />
                          <span className="text-sm font-medium text-slate-900" data-id="element-9498" data-mp="3jhjebw25fd5v">
                            {t.displayName}
                          </span>
                        </label>)}
                    </div>}

                  {schoolColleagues.length > 0 && <div data-id="element-9499" data-mp="6ybi6rnsjnbw">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9500" data-mp="6yjuahc3aom1">
                        Collega's op {schoolName}
                      </div>
                      {schoolColleagues.map(t => <label key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedTeacherId === t.id ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-200'}`} data-id="element-9501" data-mp="bvz04yvunb27">
                          <input type="radio" name="newOwner" value={t.id} checked={selectedTeacherId === t.id} onChange={e => setSelectedTeacherId(e.target.value)} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-600" data-id="element-9502" data-mp="3pa3sfu01d744" />
                          <span className="text-sm font-medium text-slate-900" data-id="element-9503" data-mp="1lt75ufvjxdvc">
                            {t.displayName}
                          </span>
                        </label>)}
                    </div>}
                </div>
              </div>

              {selectedTeacherId && <div className="p-4 bg-sky-50 border border-sky-100 rounded-xl space-y-3" data-id="element-9504" data-mp="1zrx3pxq73hkr">
                  <div className="flex items-start gap-2" data-id="element-9505" data-mp="125zgxo4s8q30">
                    <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" data-id="element-9506" data-mp="123d9rplt0clb" />
                    <div data-id="element-9507" data-mp="174kknxj8trw7">
                      <p className="text-sm font-bold text-sky-900" data-id="element-9508" data-mp="1c3dxdnqphevb">
                        Na overdracht:
                      </p>
                      <ul className="mt-1 space-y-1 text-sm text-sky-800 list-disc list-inside" data-id="element-9509" data-mp="1gv5obzdrxmlz">
                        <li data-id="element-9510" data-mp="jdou13qix2qj">
                          <span className="font-bold" data-id="element-9511" data-mp="jegepjil1g21">
                            {selectedTeacher?.displayName}
                          </span>{' '}
                          wordt de nieuwe eigenaar.
                        </li>
                        <li data-id="element-9512" data-mp="38ws3aah3n9bj">
                          Alle {classroom.learnerIds.length} leerlingen blijven
                          in de klas.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-sky-200/50" data-id="element-9513" data-mp="3hwoxssq672ei">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-9514" data-mp="otei8yaryk0">
                      <input type="checkbox" checked={keepAsCoTeacher} onChange={e => setKeepAsCoTeacher(e.target.checked)} className="mt-1 w-4 h-4 text-indigo-600 rounded border-sky-300 focus:ring-indigo-600" data-id="element-9515" data-mp="3rvnudcxnv7l5" />
                      <span className="text-sm text-sky-900" data-id="element-9516" data-mp="jgw2649cwf3r">
                        <span className="font-bold block" data-id="element-9517" data-mp="egrlad4jdid0">
                          Blijf zelf co-docent
                        </span>
                        Je behoudt inzicht in de klas, maar kunt geen
                        klasinstellingen meer wijzigen.
                      </span>
                    </label>
                  </div>
                </div>}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-9518" data-mp="o6gbzwak0eol">
                <Button onClick={onClose} variant="secondary" data-id="element-9519" data-mp="t1lv6ncknf8z">
                  Annuleren
                </Button>
                <Button onClick={handleTransfer} disabled={!selectedTeacherId} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9520" data-mp="f9z0ut26wgsu">
                  Overdragen
                </Button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}